import { useEffect, useMemo, useState } from "react";
import SearchHeader from "./components/SearchHeader";
import { challengesList } from "../../api/challenge";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import ChallengeListTable from "./components/ChallengeListTable";
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import AttachedProductImpactModal from '../../components/AttachedProductImpactModal';
const EditIcon = require("../../utils/media/icons/edit_icon.png");

const ChallengeList = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [showImpactModal, setShowImpactModal] = useState(false);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    
    const handleFilter = ({ searchTerm, activeStatus, category, complexity, fromDate, toDate }) => {
        // Check if all filters are empty
        if (!searchTerm && !activeStatus && !category && !complexity && !fromDate && !toDate) {
            setFilteredData(data); // Return all data if no filters are applied
            return;
        }

        const lowerSearchTerm = searchTerm?.toLowerCase() || '';
        const newFilteredData = data.filter((item) => {
            const matchesSearch = !searchTerm || item.name.toLowerCase().includes(lowerSearchTerm);
            const matchesStatus = !activeStatus || item.active.toString() === activeStatus;
            const matchesCategory = !category || item.category._id === category;
            const matchesComplexity = !complexity || item.complexity === complexity;
            const itemDate = new Date(item.createdAt);
            const matchesDateRange =
                (!fromDate || itemDate >= new Date(fromDate)) &&
                (!toDate || itemDate <= new Date(toDate));
            return matchesSearch && matchesStatus && matchesCategory && matchesComplexity && matchesDateRange;
        });

        setFilteredData(newFilteredData);
    };
    
    useEffect(() => {
        challengesList((fetchedData) => {
            setData(fetchedData);
            setFilteredData(fetchedData); // Initialize filtered data
            setIsLoading(false);
        }, toast, setIsLoading);
    }, []);
    
    useEffect(() => {
        if (selectedStatus) {
            const isActive = selectedStatus === "active";
            setFilteredData(data.filter(item => item.active === isActive));
        } else {
            setFilteredData(data); // Show all items when no filter is applied
        }
    }, [selectedStatus, data]);

    const handleDelete = (id) => {
        const confirm = window.confirm("Are you sure you want to delete this challenge?");
        if (!confirm) return;
        
        // Implement your delete logic here
        // deleteChallengeAPI(id, () => {
        //     toast.success("Challenge deleted successfully");
        //     // Update data after deletion
        //     setData(prevData => prevData.filter(item => item._id !== id));
        //     setFilteredData(prevFilteredData => prevFilteredData.filter(item => item._id !== id));
        // }, (error) => {
        //     toast.error(error?.message || "Failed to delete challenge");
        // });
    };

    const handleViewImpact = (challenge) => {
        setSelectedChallenge(challenge);
        setShowImpactModal(true);
    };
    
    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
            size: 80,
        }),
        columnHelper.accessor('category', {
            header: 'Category',
            cell: info => <div className="p-3">{info.getValue().name}</div>,
            size: 150,
        }),
        columnHelper.accessor('name', {
            header: 'Challenge Name',
            cell: info => <div className="font-medium">{info.getValue() || 'N/A'}</div>,
            size: 200,
        }),
        columnHelper.accessor('description', {
            header: 'Description',
            cell: info => (
                <div className="line-clamp-2 max-w-md">
                    {info.getValue() || 'N/A'}
                </div>
            ),
            size: 300,
        }),
        columnHelper.accessor('complexity', {
            header: 'Complexity',
            cell: info => {
                const complexity = info.getValue();
                let color = 'yellow';
                if (complexity === 'Easy') color = 'green';
                if (complexity === 'Hard') color = 'red';
                return <Chip value={complexity || 'N/A'} color={color} />;
            },
            size: 120,
        }),
        columnHelper.accessor('points', {
            header: 'Points',
            cell: info => info.getValue() || '0',
            size: 80,
        }),
        columnHelper.accessor('totalPages', {
            header: 'Pages/Questions',
            cell: info => info.getValue() || '0',
            size: 80,
        }),
        columnHelper.accessor('peopleJoined', {
            header: 'Opted By Users',
            cell: info => info.getValue() || '0',
            size: 80,
        }),
        columnHelper.accessor('submissionCount', {
            header: 'Submissions',
            cell: info => info.getValue() || '0',
            size: 80,
        }),
        columnHelper.accessor('createdAt', {
            header: 'Created Date',
            cell: info => info.getValue() ? new Date(info.getValue()).toLocaleString() : 'N/A',
            size: 120,
        }),
        columnHelper.accessor('active', {
            header: 'Status',
            size: 100,
            cell: info => <Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'} />,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            size: 120,
            cell: info => {
                return (
                    <div className='flex items-center justify-center'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => navigate('/challenges/challenges-main/create-challenge', { state: { id: info.row.original._id } })}
                        >
                            <img src={EditIcon} alt="edit" />
                        </button>
                        <button
                            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                            onClick={() => handleDelete(info.row.original._id)}
                        >
                            <FaTrash className="fill-chipRed" />
                        </button>
                    </div>
                )
            },
        }),
    ], [navigate]);
    
    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            <SearchHeader addNewLink={"/challenges/challenges-main/create-challenge"} onFilter={handleFilter} />
            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:pl-5 max-md:max-w-full">
                    <h1 className="self-start text-lg pl-6 pb-2 font-semibold">Challenge List</h1>
                    {isLoading ? (
                        <p className="py-4 pl-6">Loading challenges...</p>
                    ) : filteredData && filteredData.length > 0 ? (
                        // <TanStackTable
                        //     data={filteredData}
                        //     columns={columns}
                        //     enablePagination={true}
                        // />
                        <ChallengeListTable
                            data={filteredData}
                            isLoading={isLoading}
                            toast={toast}
                            setData={setData}
                            onViewImpact={handleViewImpact}
                        />
                    ) : (
                        <p className="py-4 pl-6">No challenges available</p>
                    )}
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
            
            {/* Attached Product Impact Modal */}
            <AttachedProductImpactModal
                isOpen={showImpactModal}
                onClose={() => {
                    setShowImpactModal(false);
                    setSelectedChallenge(null);
                }}
                item={selectedChallenge}
                location="challenge-details"
                contentType="challenge"
            />
        </main>
    )
}

export default ChallengeList;